
import { useState } from 'react';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface FAQ {
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    question: '¿Qué servicios ofrece PACKTO?',
    answer: 'Ofrecemos soluciones de robótica industrial personalizadas, incluyendo desarrollo de máquinas custom, renting de equipos, integración de IA, y automatización completa de procesos. Desde la idea hasta la entrega e instalación.'
  },
  {
    question: '¿Cómo funciona el renting?',
    answer: 'El renting te permite usar nuestras máquinas sin inversión inicial. Pagas una cuota mensual que incluye mantenimiento, actualizaciones y soporte técnico. Ideal para empresas que quieren automatizar sin grandes desembolsos.'
  },
  {
    question: '¿Cuánto tiempo tarda el desarrollo?',
    answer: 'Depende de la complejidad del proyecto. Proyectos simples pueden estar listos en 2-3 meses, mientras que soluciones más complejas con IA pueden requerir 6-12 meses. Tras la consulta inicial, te damos un plazo exacto.'
  },
  {
    question: '¿Qué es una máquina custom?',
    answer: 'Es una máquina diseñada específicamente para tus necesidades. Tú nos das la idea y nosotros la hacemos realidad. Estudiamos tu proceso, diseñamos la solución, fabricamos e instalamos. 100% personalizada.'
  },
  {
    question: '¿Cómo puedo contactar?',
    answer: 'Puedes escribirnos a alex@packtosolutions.com, llamar al +34 616 308 549, o usar el formulario de contacto en nuestra web. También estamos disponibles por WhatsApp.'
  },
  {
    question: '¿Precios orientativos?',
    answer: 'Los precios varían según complejidad y funcionalidades. Proyectos básicos desde 15.000€, soluciones medias 30.000-60.000€, y proyectos complejos con IA desde 80.000€. El renting desde 800€/mes. Solicita presupuesto sin compromiso.'
  },
  {
    question: '¿Qué sectores atendéis?',
    answer: 'Trabajamos en múltiples sectores: alimentación, metalurgia, logística, farmacéutica, automoción, electrónica, agricultura y cosmética. Nuestra experiencia abarca más de 150 proyectos en 12 industrias diferentes.'
  },
  {
    question: '¿Incluye mantenimiento?',
    answer: 'Sí, todos nuestros proyectos incluyen garantía y ofrecemos contratos de mantenimiento preventivo. En modalidad renting, el mantenimiento está incluido en la cuota mensual.'
  }
];

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: '¡Hola! Soy el asistente virtual de PACKTO Smart Solutions. ¿En qué puedo ayudarte hoy?',
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');

  const handleQuickQuestion = (faq: FAQ) => {
    const userMessage: Message = {
      id: messages.length + 1,
      text: faq.question,
      sender: 'user',
      timestamp: new Date()
    };

    const botMessage: Message = {
      id: messages.length + 2,
      text: faq.answer,
      sender: 'bot',
      timestamp: new Date()
    };

    setMessages([...messages, userMessage, botMessage]);
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: messages.length + 1,
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };

    const matchedFAQ = faqs.find(faq => 
      inputValue.toLowerCase().includes(faq.question.toLowerCase().split(' ')[1]) ||
      inputValue.toLowerCase().includes('servicio') ||
      inputValue.toLowerCase().includes('renting') ||
      inputValue.toLowerCase().includes('tiempo') ||
      inputValue.toLowerCase().includes('precio') ||
      inputValue.toLowerCase().includes('contacto') ||
      inputValue.toLowerCase().includes('custom') ||
      inputValue.toLowerCase().includes('sector') ||
      inputValue.toLowerCase().includes('mantenimiento')
    );

    const botResponse = matchedFAQ 
      ? matchedFAQ.answer 
      : 'Gracias por tu consulta. Para una respuesta más detallada, te recomiendo contactar directamente con nuestro equipo en alex@packtosolutions.com o al +34 616 308 549. También puedes usar las preguntas rápidas para obtener información inmediata.';

    const botMessage: Message = {
      id: messages.length + 2,
      text: botResponse,
      sender: 'bot',
      timestamp: new Date()
    };

    setMessages([...messages, userMessage, botMessage]);
    setInputValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-24 right-6 z-50 w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center cursor-pointer"
        aria-label="Abrir chat"
      >
        {isOpen ? (
          <i className="ri-close-line text-2xl"></i>
        ) : (
          <i className="ri-chat-3-line text-2xl"></i>
        )}
      </button>

      {/* Ventana de chat */}
      {isOpen && (
        <div className="fixed bottom-40 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-12rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-green-500 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-robot-2-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-base whitespace-nowrap">Asistente PACKTO</h3>
                <p className="text-xs text-white/90 whitespace-nowrap">Siempre disponible</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"
              aria-label="Cerrar chat"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Mensajes */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    message.sender === 'user'
                      ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 rounded-bl-sm shadow-sm'
                  }`}
                >
                  <p className="text-sm leading-relaxed">{message.text}</p>
                  <p className={`text-xs mt-1 ${message.sender === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                    {message.timestamp.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Preguntas rápidas */}
          <div className="p-3 bg-white border-t border-gray-200 overflow-x-auto">
            <p className="text-xs text-gray-600 mb-2 whitespace-nowrap">Preguntas frecuentes:</p>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {faqs.slice(0, 4).map((faq, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickQuestion(faq)}
                  className="px-3 py-1.5 bg-gradient-to-r from-orange-50 to-green-50 text-orange-600 rounded-full text-xs hover:from-orange-100 hover:to-green-100 transition-colors whitespace-nowrap cursor-pointer border border-orange-200"
                >
                  {faq.question}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu pregunta..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <button
                onClick={handleSendMessage}
                className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-full hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center justify-center cursor-pointer whitespace-nowrap"
                aria-label="Enviar mensaje"
              >
                <i className="ri-send-plane-fill text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
